import React, { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { CardMedia } from "@/components/CardImage";
import { AvailabilityFormDialog } from "@/components/AvailabilityFormDialog";
import { ConditionBadge, Tag } from "@/components/Badges";
import { VerifyBanner } from "@/components/VerifyBanner";
import { Loading, EmptyState } from "@/components/States";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Plus, Pencil, Archive, Trash2, Layers } from "lucide-react";
import { toast } from "sonner";

export default function MyCards() {
  const { user } = useAuth();
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/availabilities/mine").then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setOpen(true); };
  const openEdit = (a) => { setEditing(a); setOpen(true); };

  const archive = async (id) => { try { await api.post(`/availabilities/${id}/archive`); toast.success("Carta archiviata."); load(); } catch (e) { toast.error(apiError(e)); } };
  const remove = async (id) => { try { await api.delete(`/availabilities/${id}`); toast.success("Carta rimossa."); load(); } catch (e) { toast.error(apiError(e)); } };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div><h1 className="font-display text-3xl font-bold text-[#F8FAFC]">Le mie carte</h1><p className="mt-1 text-[#CBD5E1]">Le carte che possiedi e sei disposto a scambiare.</p></div>
        <Button onClick={openNew} disabled={!user?.email_verified} data-testid="add-card-button" className="bg-[#7C3AED] hover:bg-[#6D28D9]"><Plus className="mr-2 h-4 w-4" />Aggiungi una carta</Button>
      </div>
      <div className="mt-6"><VerifyBanner /></div>

      {items === null ? <Loading /> : items.length === 0 ? (
        <EmptyState icon={Layers} title="Nessuna carta pubblicata" description="Aggiungi la tua prima carta disponibile allo scambio, con almeno una foto reale, per iniziare a trovare corrispondenze."
          action={<Button onClick={openNew} disabled={!user?.email_verified} className="bg-[#7C3AED] hover:bg-[#6D28D9]"><Plus className="mr-2 h-4 w-4" />Aggiungi una carta</Button>} />
      ) : (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4" data-testid="my-cards-grid">
          {items.map((a) => (
            <div key={a.id} className="rounded-xl border border-[#2A3655] bg-[#141B2D] p-2" data-testid="my-card-item">
              <CardMedia photos={a.photos} card={a.card} />
              <div className="space-y-1 px-1 pb-1 pt-2">
                <p className="line-clamp-1 text-sm font-semibold text-[#F8FAFC]">{a.card?.name}</p>
                <p className="text-xs text-[#94A3B8]">{a.card?.expansion_name || "—"}</p>
                <div className="flex flex-wrap gap-1 pt-1"><ConditionBadge value={a.condition} /><Tag>{a.language}</Tag><Tag tone="#94A3B8">×{a.quantity}</Tag></div>
                {!a.public && <Tag tone="#F59E0B">Privata</Tag>}
                {a.card?.is_draft && <Tag tone="#F59E0B">Bozza (esclusa dal matching)</Tag>}
                <div className="flex items-center gap-1 pt-2">
                  <button onClick={() => openEdit(a)} data-testid="edit-card" className="rounded p-1.5 text-[#CBD5E1] hover:bg-[#1C2640]"><Pencil className="h-3.5 w-3.5" /></button>
                  <button onClick={() => archive(a.id)} data-testid="archive-card" className="rounded p-1.5 text-[#CBD5E1] hover:bg-[#1C2640]"><Archive className="h-3.5 w-3.5" /></button>
                  <button onClick={() => remove(a.id)} data-testid="delete-card" className="rounded p-1.5 text-[#CBD5E1] hover:bg-[#EF4444]/20 hover:text-[#EF4444]"><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <AvailabilityFormDialog open={open} onOpenChange={setOpen} editing={editing} onSaved={load} />
    </div>
  );
}
